import { createContext, useContext, useState } from 'react'
import { queryClient, trpc, type RouterOutput } from '@/config/trpc'
import { useUserContext } from './userLogin'

type Brand = RouterOutput['brand']['list'][number]

type BrandContextValue = {
  brands: Brand[]
  currentBrand: Brand | null
  isLoading: boolean
  isSwitching: boolean
  switchingToBrandId: number | null
  switchBrand: (brandId: number) => Promise<void>
  createBrand: (name: string) => Promise<void>
}

const BrandContext = createContext<BrandContextValue | null>(null)

export const BrandProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, updateSessionToken } = useUserContext()
  const utils = trpc.useUtils()
  const [switchingToBrandId, setSwitchingToBrandId] = useState<number | null>(null)

  const { data: brands = [], isLoading: isListLoading } = trpc.brand.list.useQuery(undefined, {
    enabled: !!user,
  })

  const { data: currentBrand, isLoading: isCurrentLoading } = trpc.brand.getCurrent.useQuery(
    undefined,
    {
      enabled: !!user && brands.length > 0,
      retry: false,
    },
  )

  const { mutateAsync: switchBrandMutation } = trpc.brand.switch.useMutation()

  const { mutateAsync: createBrandMutation } = trpc.brand.create.useMutation({
    onSuccess: () => {
      utils.brand.list.invalidate()
    },
  })

  const switchBrand = async (brandId: number) => {
    setSwitchingToBrandId(brandId)
    try {
      const { sessionToken } = await switchBrandMutation({ brandId })
      if (sessionToken) {
        updateSessionToken(sessionToken)
      }
      // Drop the cache rather than invalidating it. The active brand rides in the
      // session token, never in the query key, so every cached entry belongs to the
      // brand we just left. `invalidate` keeps that data readable while it refetches,
      // which is how a freshly created brand ended up resuming the previous brand's
      // moodboard (its id 404s, and the canvas then sat on "Loading…" forever).
      queryClient.clear()
      await Promise.all([utils.brand.getCurrent.fetch(), utils.brand.list.fetch()])
    } finally {
      setSwitchingToBrandId(null)
    }
  }

  const createBrand = async (name: string) => {
    const result = await createBrandMutation({ name })
    if (!result.ok) {
      throw new Error('Usage limit exceeded')
    }
    // Switch to the new brand
    await switchBrand(result.id)
  }

  return (
    <BrandContext.Provider
      value={{
        brands,
        currentBrand: currentBrand ?? null,
        isLoading: isListLoading || isCurrentLoading,
        isSwitching: switchingToBrandId !== null,
        switchingToBrandId,
        switchBrand,
        createBrand,
      }}>
      {children}
    </BrandContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useBrand = () => {
  const context = useContext(BrandContext)
  if (!context) throw new Error('useBrand must be used within BrandProvider')
  return context
}
